import { useState, useContext, useRef, useEffect, useCallback } from 'react';

import '../../FinancialBuilder/DashboardOverview/DashboardOverview.css';
import { DollarOutlined, TableOutlined, CalculatorFilled, ProductFilled, DollarCircleFilled, BookFilled, SettingFilled } from '@ant-design/icons';
import { PSSBPageContext } from '../PSBBPage.jsx';
import { ShowAndHide } from '../../helperFunctions.jsx';

import { genericPOSTDatabaseCall, SidebarButton, RenderOneTable, RenderMultiSelectTable } from '../../helperFunctions.jsx'
import { psbbOtherVariablesRenderFields } from '../../constants.jsx';
import { EntityDetails } from '../EntitiesPage/EntitiesPage.jsx';

import { RevenueVsCostChart, FinancialProjectionChart, RevenueGrowthChart, CostOverviewChart, BurnRateChart} from '../../FinancialBuilder/Charts/Charts.js'


import InvestmentEquityModal from '../../CriticalFinance/Misc/InvestmentEquityModal.jsx';
import CapTableSettingsModal from '../../CriticalFinance/Misc/CapTableSettingsModal.jsx';
import ProductsPage from '../../PSBB/ProductsPage/ProductsPage.jsx';
import FixedCostsPage from '../../FinancialBuilder/FixedCostsPage/FixedCostsPage.jsx';
import OtherVariablesCost from '../../FinancialBuilder/Misc/OtherVariblesCost.jsx';
import CostAllocations from '../../FinancialBuilder/Misc/CostAllocations.jsx';

// <SidebarButton icon={<SettingFilled className='sidebar-icon' />} itemInModal={<SettingsPage />} />

function DashboardSideBar({ onClickUpdateCalc }) {
    const sidebarRef = useRef();

    document.addEventListener('scroll', () => {
        if (sidebarRef.current) {
            if (window.scrollY < 25) sidebarRef.current.style.top = `50px`;
            else sidebarRef.current.style.top = `${window.scrollY - 25}px`;
        }
    });

    return (
        <div className="fb-dashboard-sidebar" ref={sidebarRef}>
            <SidebarButton name={"Financials"} icon={<DollarOutlined className='sidebar-icon' />} itemInModal={<InvestmentEquityModal contextToUse={PSSBPageContext} usedAt='psbb' />} />
            <SidebarButton name={"Data Tables"} icon={<TableOutlined className='sidebar-icon' />} itemInModal={<CapTableSettingsModal contextToUse={PSSBPageContext} />} />
            <SidebarButton name={"Products"} icon={<ProductFilled className='sidebar-icon' />} itemInModal={<ProductsPage showNavigationControls={false} />} />
            <SidebarButton name={"Cost Allocations"} icon={<DollarCircleFilled className='sidebar-icon' />} itemInModal={<CostAllocations contextToUse={PSSBPageContext} />} />
            <SidebarButton name={"Fixed Costs"} icon={<BookFilled className='sidebar-icon' />} itemInModal={<FixedCostsPage showNavigationControls={false} contextToUse={PSSBPageContext} />} />
            <SidebarButton name={"Other Variables Cost"} icon={<SettingFilled className='sidebar-icon' />} itemInModal={<OtherVariablesCost renderFields={psbbOtherVariablesRenderFields} contextToUse={PSSBPageContext} />} />
            <SidebarButton onClickUpdateCalc={onClickUpdateCalc} isModal={false} name={"Calculate"} icon={<CalculatorFilled className='sidebar-icon' />} />
        </div>
    );
}

function CapTable({ data }) {
    return (
        <div>
            <table className="entity-table">
                <thead>
                    <tr>
                        <th>Shareholders</th>
                        <th>Share %</th>
                        <th>USD (PV)</th>
                    </tr>
                </thead>
                <tbody>
                    {data.map((item, index) => (
                        <tr key={index}>
                            <td>{item.name}</td>
                            <td>{item.sharePercentage}</td>
                            <td>{item.presentValue}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function Dashboard() {
    const { setPageNum, entitySelected, messageApi, setDataForExport, dataForExport, useInvestorReadyFinancial } = useContext(PSSBPageContext);
    const [dataLoading, setDataLoading] = useState(true);
    const [calculationData, setCalculationData] = useState({
        investment_data: [],
        share_price_data: [],
        valuation_at_ipo: [],
        cap_table_data: []
    });
    const [formattedData, setFormattedData] = useState([["A","A"],["A","A"]]);
    const [percentageData, setPercentageData] = useState([["A","A"],["A","A"]]);
    const [firm_wide_data, setFirm_wide_data] = useState({});
    const [productAnalysisData, setProductAnalysisData] = useState([]);

    const getUpdatedCalculations = useCallback(async () => {
        messageApi.loading('Fetching updated calculations...', 0);
        try {
            const response = await genericPOSTDatabaseCall('/api/psbb/getCalculationData', { entity: entitySelected, useInvestorReadyFinancial: useInvestorReadyFinancial }, "calculation data");
            console.log('Updated calculation data:', response);
            setCalculationData(response.data);
            setDataForExport({...response.data, 'currency': entitySelected.out_currency}); // Set data for export
            setFormattedData(response.data.formattedData);
            setPercentageData(response.data.percentage_data);
            setFirm_wide_data({
                firmWideStat: response.data.firm_wide_stat,
                firmWideStatPercentage: response.data.firm_wide_stat_percentage,
                otherFirmWideStat: response.data.other_firm_wide_stat,
                arrRatiosFirmWide: response.data.arr_ratios_firm_wide
            });
            setProductAnalysisData(response.data.productAnalyses);
            messageApi.destroy();
            messageApi.success('Calculations updated successfully');
        } catch (error) {
            console.error('Error fetching calculation data:', error);
            messageApi.destroy();
            messageApi.error('Failed to fetch updated calculations');
        }
        setDataLoading(false); // Only set to false after first load
    }, [entitySelected, messageApi, setDataForExport])

    useEffect(() => {
        getUpdatedCalculations();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="fb-dashboard-overview financial-builder-container entity-industry-container">
            <h2>Summary Page</h2>
            <ShowAndHide title={"Entity Info"}>
                <EntityDetails entity={entitySelected} editable={false} />
            </ShowAndHide>
            {!dataLoading &&
                <>
                    <ShowAndHide defaultOpen title="Financial Information Analysis Firm Wide" className="fb-dashboard-overview-content">
                        <div className='flex-row flex-space-around'>
                            <RenderOneTable name={"Firm Wide Stat %"} data={firm_wide_data.firmWideStatPercentage} />
                            <RenderOneTable name={"Employees & Price Firm Wide Stat %"} data={firm_wide_data.firmWideStat} />
                            <RenderOneTable name={"Other Firm Wide Stat"} data={firm_wide_data.otherFirmWideStat} />
                            <RenderOneTable name={"ARR Ratios Firm Wide"} data={firm_wide_data.arrRatiosFirmWide} />
                        </div>
                    </ShowAndHide>
                    <ShowAndHide defaultOpen title="Projections & Growth" className="fb-dashboard-overview-content">
                        <RenderMultiSelectTable data={formattedData} />
                        <RenderMultiSelectTable data={percentageData} />
                    </ShowAndHide>
                    <ShowAndHide defaultOpen title="Financial Projections" className="fb-dashboard-overview-content">
                        <FinancialProjectionChart finalDATA={{ formattedData, percentageData }} />
                    </ShowAndHide>
                    <ShowAndHide defaultOpen title="CFI - Valuations, Investments, Pricing and Equity" className="fb-dashboard-overview-content">
                        <div className='flex-row flex-space-around'>
                            <RenderOneTable data={calculationData.investment_data} name={"Investment Consideration"} />
                            <RenderOneTable data={calculationData.share_price_data} name={"Price your Share"} />
                        </div>
                    </ShowAndHide>
                    <ShowAndHide defaultOpen title="Valuations & Cap Tables (@IPO)" className="fb-dashboard-overview-content">
                        <div className='flex-row flex-space-around'>
                            <RenderOneTable data={calculationData.valuation_at_ipo} name={"Valuation at IPO"} />
                            <CapTable data={calculationData.cap_table_data} />
                        </div>
                    </ShowAndHide>
                    <ShowAndHide defaultOpen title="Product Analysis" className="fb-dashboard-overview-content">
                        <div className="flex-row flex-space-around">
                            {productAnalysisData.map((productAnalysis, index) => (
                                <RenderOneTable key={index} data={productAnalysis.data} name={`Product: ${productAnalysis.name}`} />
                            ))}
                        </div>
                    </ShowAndHide>
                    <ShowAndHide defaultOpen title="Revenue Growth By Product %" className="fb-dashboard-overview-content">
                        <RevenueGrowthChart finalDATA={{ formattedData, percentageData }} />
                    </ShowAndHide>
                    <ShowAndHide defaultOpen title="Fixed vs Variable Costs" className="fb-dashboard-overview-content">
                        <CostOverviewChart finalDATA={{ formattedData, percentageData }} />
                    </ShowAndHide>
                    <ShowAndHide defaultOpen title="Revenues & Cost of Revenue %" className="fb-dashboard-overview-content">
                        <RevenueVsCostChart finalDATA={{ formattedData, percentageData }} />
                    </ShowAndHide>
                    <ShowAndHide defaultOpen title="Burn Rate (Monthly) & Burn rate % (Usage)" className="fb-dashboard-overview-content">
                        <BurnRateChart finalDATA={{ formattedData, percentageData }} />
                    </ShowAndHide>
                </>
            }
            <div className="navigation-button-container">
                <button type="button" onClick={() => setPageNum(2)} className="next-button">Back</button>
                <button type="button" disabled={!dataForExport} onClick={() => setPageNum(4)} className="next-button">Next</button>
            </div>
            <DashboardSideBar onClickUpdateCalc={getUpdatedCalculations} />
        </div>
    )
}

export default Dashboard;